/**
 * useFeedback - Initializes the feedback/survey integration for the app.
 *
 * Call once from App.vue. Handles:
 * - Feature gate (app.config features.feedback boolean + runtimeConfig)
 * - FormbricksHandler setup
 * - Route change tracking
 * - User identification: identifies with subscriber ID when userStore has a subscriber,
 *   resets on logout
 */

import FeedbackService from '~/lib/services/feedback/FeedbackService';
import { FormbricksHandler } from '~/lib/services/feedback/FormbricksHandler';

export function useFeedback() {
  const isInitialized = useState('feedback:initialized', () => false);
  const appConfig = useAppConfig();
  const runtimeConfig = useRuntimeConfig();
  const router = useRouter();
  const userStore = useUserStore();
  const subscriptionStore = useSubscriptionStore();
  const { locale } = useI18n();

  onMounted(() => {
    if (isInitialized.value) return;

    const feedbackEnabled = appConfig.features.feedback;
    const feedbackEnv = runtimeConfig.public.feedback;

    if (!feedbackEnabled || !feedbackEnv?.enabled) {
      console.debug('[Feedback] Disabled via feature toggle or environment config');
      return;
    }

    if (!feedbackEnv.environmentId) {
      console.warn('[Feedback] FORMBRICKS_ENVIRONMENT_ID not configured, skipping initialization');
      return;
    }

    const service = new FeedbackService();
    const handler = new FormbricksHandler({
      environmentId: feedbackEnv.environmentId,
      appUrl: feedbackEnv.appUrl,
    });

    service.setHandler(handler);
    isInitialized.value = true;

    router.afterEach(() => {
      service.registerRouteChange();
    });

    function buildAttributes(): Record<string, string> {
      const attributes: Record<string, string> = {};
      if (subscriptionStore.selectedSubscription?.plan?.code) attributes.plan = subscriptionStore.selectedSubscription.plan.code;
      return attributes;
    }

    watch(
      locale,
      (newLocale) => {
        if (newLocale) {
          service.setLanguage(newLocale);
        }
      },
      { immediate: true },
    );

    watchEffect(() => {
      const subscriber = userStore.subscriber;
      if (subscriber?.id) {
        service.identify(subscriber.id, buildAttributes());
      } else {
        service.reset();
      }
    });
  });
}
